import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatNdc, US_STATES } from '../constants'
import {
  Badge,
  CameraIcon,
  CheckIcon,
  ChevronDownIcon,
  CloseIcon,
  cn,
  Heading,
  Label,
  Paragraph,
  PrescriptionBottleIcon,
  Tooltip,
} from '@quickmd-llc/design-system'
import { MEDICATIONS_EDIT_ROUTE } from '@/utils/constants'
import type {
  FulfillmentPartnerAdminApiContract,
  MedicationStaffApiOutContract,
} from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import { useGetAdminsFulfillmentpartners } from '@/api/generated/fulfillment-partner/fulfillment-partner'

/* ── Partner accordion ── */

interface PartnerAccordionProps {
  partner: FulfillmentPartnerAdminApiContract
}

function PartnerAccordion({ partner }: PartnerAccordionProps) {
  const [expanded, setExpanded] = useState(false)
  const unsupported = (partner.ExcludedStateCodes ?? []).length
  const supported = US_STATES.length - unsupported

  return (
    <div className='rounded-large border border-border-medium'>
      <button
        type='button'
        className='flex w-full items-center justify-between px-sp-3 py-sp-2 text-left'
        onClick={() => setExpanded((v) => !v)}>
        <Label size='lg'>{partner.Name}</Label>
        <ChevronDownIcon
          iconSize='s'
          className={cn('text-text-light transition-transform', expanded && 'rotate-180')}
        />
      </button>

      {expanded ?
        <div className='border-t border-border-medium px-sp-3 pb-sp-3 pt-sp-2'>
          {/* Shipping options */}
          {partner.ShippingOptions && partner.ShippingOptions.length > 0 ?
            <div className='mb-sp-2 flex flex-col gap-sp-2'>
              <Label size='sm' className='text-text-medium'>
                Shipping options
              </Label>
              <div className='flex flex-col gap-sp-2'>
                {partner.ShippingOptions.map((opt) => (
                  <div key={opt.Name} className='flex items-center justify-between'>
                    <Paragraph size='sm' className='text-text-dark'>
                      {opt.Name} ({opt.MinimumDays}-{opt.MaximumDays} days)
                    </Paragraph>
                    <Label size='md'>{opt.Price === 0 ? 'Free' : `$${opt.Price.toFixed(2)}`}</Label>
                  </div>
                ))}
              </div>
            </div>
          : null}

          <hr className='-mx-sp-3 border-border-medium' />

          {/* State support summary */}
          <div className='mb-sp-2 mt-sp-2 flex items-center gap-sp-2'>
            <span className='flex items-center gap-sp-0.5'>
              <CheckIcon iconSize='s' className='text-text-success' />
              <Label size='md' className='text-text-success'>
                {supported} supported states
              </Label>
            </span>
            {unsupported > 0 && (
              <span className='flex items-center gap-sp-0.5'>
                <CloseIcon iconSize='s' className='text-text-error' />
                <Label size='md' className='text-text-error'>
                  {unsupported} not supported
                </Label>
              </span>
            )}
          </div>

          {/* State badges grid */}
          <div className='rounded-large border border-border-medium bg-neutral-5 p-sp-2'>
            <div className='flex flex-wrap gap-sp-1'>
              {US_STATES.map((state) => {
                const isSupported = !(partner.ExcludedStateCodes ?? []).includes(state)
                return (
                  <span
                    key={state}
                    className={cn(
                      'inline-flex items-center gap-0.5 rounded-full border bg-white px-1.5 py-0.5',
                      isSupported ? 'border-text-success' : 'border-text-error',
                    )}>
                    {isSupported ?
                      <CheckIcon iconSize='s' className='text-text-success' />
                    : <CloseIcon iconSize='s' className='text-text-error' />}
                    <Label
                      size='sm'
                      className={cn('text-[11px]', isSupported ? 'text-text-success' : 'text-text-error')}>
                      {state}
                    </Label>
                  </span>
                )
              })}
            </div>
          </div>
        </div>
      : null}
    </div>
  )
}

/* ── Main detail view ── */

interface MedicationDetailViewProps {
  medication: MedicationStaffApiOutContract
  onDelete: (medication: MedicationStaffApiOutContract) => void
}

export function MedicationDetailView({ medication, onDelete }: MedicationDetailViewProps) {
  const navigate = useNavigate()
  const { data: partners } = useGetAdminsFulfillmentpartners()

  const linkedPartners = (partners ?? []).filter((p) =>
    (medication.FulfillmentPartnerNcpdpIds ?? []).includes(p.NcpdpId ?? ''),
  )

  const primaryCode =
    medication.NationalDrugCode ? `NDC: ${formatNdc(medication.NationalDrugCode)}`
    : medication.GenericCodeNumber ? `GCN: ${medication.GenericCodeNumber}`
    : null

  return (
    <div className='h-full overflow-y-auto rounded-large border border-border-medium bg-white p-sp-3'>
      {/* Header: image + name + buttons */}
      <div className='mb-sp-3 flex items-start gap-sp-2'>
        {/* Medication image */}
        <div className='flex h-sp-16 w-sp-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-larger border border-border-medium bg-background'>
          {medication.ImageUrl ?
            <img src={medication.ImageUrl} alt={medication.Name ?? ''} className='h-full w-full object-cover' />
          : <CameraIcon iconSize='xl' className='text-neutral-40' />}
        </div>

        {/* Name + code */}
        <div className='flex-1'>
          <div className='flex items-center gap-sp-1'>
            <Heading level='h2'>{medication.Name}</Heading>
            {medication.IsControlledSubstance ?
              <Tooltip content='Controlled substance'>
                <PrescriptionBottleIcon iconSize='m' className='text-highlight' />
              </Tooltip>
            : null}
          </div>
          {primaryCode ?
            <Badge variant='idle' size='s' className='mt-sp-0.5'>
              {primaryCode}
            </Badge>
          : null}
        </div>

        {/* Edit / Delete pill buttons */}
        <div className='flex gap-sp-1'>
          <button
            type='button'
            className='rounded-full bg-surface-highlight px-sp-2 py-sp-0.5 transition-colors hover:bg-neutral-20'
            onClick={() => navigate(`${MEDICATIONS_EDIT_ROUTE}/${medication.Id}`)}>
            <Label size='md' className='text-highlight'>
              Edit
            </Label>
          </button>
          <button
            type='button'
            className='rounded-full bg-surface-error px-sp-2 py-sp-0.5 transition-colors hover:bg-red-20'
            onClick={() => onDelete(medication)}>
            <Label size='md' className='text-text-error'>
              Delete
            </Label>
          </button>
        </div>
      </div>

      {/* Amount and pricing */}
      {medication.Formulations && medication.Formulations.length > 0 ?
        <div className='mb-sp-3'>
          <Label size='sm' className='mb-sp-1.5 block text-text-medium'>
            Amount and pricing
          </Label>
          <div className='flex flex-wrap gap-sp-2'>
            {medication.Formulations.map((f) => (
              <div
                key={`${f.TotalQuantity}-${f.Price}`}
                className='w-[calc((100%_-_0.5rem)_/_3)] rounded-large bg-neutral-10 p-sp-2'>
                <Paragraph size='sm' className='text-text-medium'>
                  {f.TotalQuantity} {(medication.DispenseUnit ?? 'units').toLowerCase()}
                </Paragraph>
                <Label size='xl' className='text-highlight'>
                  ${f.Price.toFixed(2)}
                </Label>
              </div>
            ))}
          </div>
        </div>
      : null}

      {/* Category */}
      {medication.Category ?
        <div className='mb-sp-3'>
          <Label size='sm' className='mb-sp-1.5 block text-text-medium'>
            Category
          </Label>
          <div className='rounded-large border border-border-medium px-sp-3 py-sp-2'>
            <Label size='lg'>{medication.Category as unknown as string}</Label>
          </div>
        </div>
      : null}

      {/* Patient-facing description */}
      {medication.PatientDescription ?
        <div className='mb-sp-3'>
          <Label size='sm' className='mb-sp-1.5 block text-text-medium'>
            Patient-facing description
          </Label>
          <div className='rounded-large border border-border-medium p-sp-2'>
            <p className='text-size-3 leading-5 text-text-dark'>{medication.PatientDescription}</p>
          </div>
        </div>
      : null}

      {/* Fulfillment partners */}
      {linkedPartners.length > 0 && (
        <div>
          <Label size='sm' className='mb-sp-1.5 block text-text-medium'>
            Fulfillment partners
          </Label>
          <div className='flex flex-col gap-sp-1.5'>
            {linkedPartners.map((partner) => (
              <PartnerAccordion key={partner.NcpdpId} partner={partner} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
